// No authentication endpoints here.
//
// The wine-api deleted its entire /admin/auth/* tree — admin identity lives in
// nota-main-api and the wine-api is now a pure resource server that verifies
// tokens and never handles credentials. Every auth call goes through
// `services/notaAuthService`, which talks to main-api directly.
//
// What remains in this module is local session *state* (reading the cached
// user, role predicates, redirect computation) — no network auth calls.

export type AdminRole = 'SYSTEM_ADMIN' | 'ADMIN' | 'VENUE_USER';

export interface AdminUser {
  id?: string; // Keep for backward compatibility
  _id: string; // API returns _id instead of id
  email: string;
  firstName: string;
  lastName: string;
  role: AdminRole;
  // Required (non-empty) when role === VENUE_USER. Empty/absent for ADMIN/SYSTEM_ADMIN.
  assignedVenueIds?: string[];
  // COSTING.1d — stock permission keys (e.g. 'stock-costs:view'). Drives client
  // cost display-gating; the server strips cost fields regardless.
  permissions?: string[];
  passwordChangeRequired: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Session {
  id: string;
  userId: string;
  deviceInfo: {
    deviceId: string;
    deviceName?: string;
    browser?: string;
    os?: string;
    ip?: string;
    lastUsed?: string;
  };
  isValid: boolean;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Normalize an AdminUser so both `id` and `_id` are populated.
 *
 * The /admin/auth/login response uses `id` (a stringified ObjectId via
 * adminUserController.getUserData), while the /admin/admin-user CRUD
 * endpoints return `_id` directly from Mongoose's toObject(). Without
 * normalization, AuthContext's `user._id` is undefined right after login,
 * which breaks change-password (and any other place that reaches for `_id`).
 *
 * Normalize at the auth boundary so callers can trust either field.
 */
const normalizeAdminUser = <T extends { id?: string; _id?: string }>(u: T): T => {
  if (!u) return u;
  if (!u._id && u.id) return { ...u, _id: u.id };
  if (!u.id && u._id) return { ...u, id: u._id };
  return u;
};

const authService = {
  /**
   * Check if user is authenticated
   */
  isAuthenticated: (): boolean => {
    return !!localStorage.getItem('access_token');
  },

  /**
   * Get current user
   */
  getCurrentUser: (): AdminUser | null => {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    // Defensive normalization — sessions that were logged in before the
    // login() normalization shipped may have only `id` stored. Keeping this
    // here means consumers can always rely on `_id`.
    return normalizeAdminUser(JSON.parse(userStr) as AdminUser);
  },

  /**
   * Check if current user has admin role (SYSTEM_ADMIN or ADMIN — full app access).
   * Returns false for VENUE_USER (venue-scoped only).
   */
  isAdmin: (): boolean => {
    const user = authService.getCurrentUser();
    if (!user) return false;
    return user.role === 'SYSTEM_ADMIN' || user.role === 'ADMIN';
  },

  /**
   * Check if current user has system admin role.
   */
  isSystemAdmin: (): boolean => {
    const user = authService.getCurrentUser();
    return user?.role === 'SYSTEM_ADMIN';
  },

  /**
   * Check if current user is a venue-scoped user (limited to assigned venues).
   */
  isVenueUser: (): boolean => {
    const user = authService.getCurrentUser();
    return user?.role === 'VENUE_USER';
  },

  /**
   * COSTING.1d — can the current user see recorded stock cost? ADMIN/SYSTEM_ADMIN
   * by role; VENUE_USER only with the 'stock-costs:view' permission (default-
   * hidden). Display-gating only — the server strips cost fields authoritatively.
   */
  canViewStockCosts: (): boolean => {
    const user = authService.getCurrentUser();
    if (!user) return false;
    if (user.role === 'SYSTEM_ADMIN' || user.role === 'ADMIN') return true;
    return (user.permissions ?? []).includes('stock-costs:view');
  },

  /**
   * Returns true if the current user can access the given venueId.
   * SYSTEM_ADMIN and ADMIN have implicit access to all venues. VENUE_USER is
   * limited to their assignedVenueIds.
   */
  canAccessVenue: (venueId: string): boolean => {
    const user = authService.getCurrentUser();
    if (!user) return false;
    if (user.role === 'SYSTEM_ADMIN' || user.role === 'ADMIN') return true;
    if (user.role === 'VENUE_USER') {
      return (user.assignedVenueIds ?? []).map(String).includes(String(venueId));
    }
    return false;
  },

  /**
   * Computes where to send a user after a successful login.
   *
   * - SYSTEM_ADMIN / ADMIN → `from` if present, else `/admin`. They can roam
   *   freely so honoring the URL they tried to visit before login is fine.
   * - VENUE_USER → ignore `from` (it might point to a forbidden global page).
   *   1 assigned venue → straight into that venue dashboard.
   *   2+ assigned venues → the picker page at `/admin/my-venues`.
   *   0 assigned venues → `/unauthorized` (data is misconfigured; they
   *   shouldn't be able to log in but the backend pre-validate hook should
   *   already prevent this).
   *
   * Reads from authService.getCurrentUser(), so call this AFTER the login
   * promise resolves — by then localStorage.user is set.
   */
  computePostLoginRedirect: (from?: string | null): string => {
    const user = authService.getCurrentUser();
    if (!user) return '/admin';
    if (user.role === 'VENUE_USER') {
      const venues = user.assignedVenueIds ?? [];
      if (venues.length === 0) return '/unauthorized';
      if (venues.length === 1) return `/admin/venues/${venues[0]}`;
      return '/admin/my-venues';
    }
    return from || '/admin';
  },
};

export default authService;
